import { useState, useCallback, useRef } from 'react';
import { BLE_CONFIG } from './bleConfig';
import {
  parseBluetoothData,
  checkBluetoothSupport,
  MESSAGE_TYPES,
  sendCommand,
  COMMANDS
} from './bluetoothUtils';

export const useMultipleBluetooth = (deviceSettings = {}) => {
  const [devices, setDevices] = useState({
    left: {
      device: null,
      isConnected: false,
      isRecording: false,
      sensorValues: {},
      imu: null,
      onPointe: false,
      lastMessage: null,
      calibrationState: null,
      status: null
    },
    right: {
      device: null,
      isConnected: false,
      isRecording: false,
      sensorValues: {},
      imu: null,
      onPointe: false,
      lastMessage: null,
      calibrationState: null,
      status: null
    }
  });

  const [isConnecting, setIsConnecting] = useState({ left: false, right: false });
  const [events, setEvents] = useState({ left: [], right: [] });

  const characteristicsRef = useRef({ left: null, right: null });
  const isRecordingRef = useRef({ left: false, right: false });
  const eventCallbacksRef = useRef({ left: null, right: null });

  const handleDataPacket = useCallback((data, foot) => {
    const currentRecordingState = isRecordingRef.current[foot];

    const sensorValuesObj = {};
    data.sensors.forEach((val, index) => {
      sensorValuesObj[index + 1] = val;
    });

    if (!currentRecordingState) {
      setDevices(prev => ({
        ...prev,
        [foot]: {
          ...prev[foot],
          imu: data.imu,
          onPointe: data.onPointe,
          sensorValues: sensorValuesObj,
        }
      }));
      return;
    }

    console.log(`📊 ${foot} foot data:`, {
      sensors: {
        sensorValues: sensorValuesObj,
        count: data.sensors.length,
        min: Math.min(...data.sensors),
        max: Math.max(...data.sensors),
        avg: Math.round(data.sensors.reduce((a, b) => a + b, 0) / data.sensors.length)
      },
      imu: data.imu,
      onPointe: data.onPointe,
    });

    setDevices(prev => ({
      ...prev,
      [foot]: {
        ...prev[foot],
        sensorValues: sensorValuesObj,
        imu: data.imu,
        onPointe: data.onPointe
      }
    }));
  }, []);

  const handleTextMessage = useCallback((data, foot) => {
    console.log(`📨 ${foot} foot message:`, data.raw, data);

    setDevices(prev => ({
      ...prev,
      [foot]: {
        ...prev[foot],
        lastMessage: data
      }
    }));

    switch (data.event) {
      case 'connected':
      case 'deviceReady':
        console.log(`✅ ${foot} foot device ready`);
        break;

      case 'pointeChange':
        setDevices(prev => ({
          ...prev,
          [foot]: {
            ...prev[foot],
            onPointe: data.onPointe
          }
        }));
        addEvent(foot, { type: 'pointeChange', onPointe: data.onPointe, timestamp: data.timestamp });
        break;

      case 'jump':
        addEvent(foot, { type: 'jump', magnitude: data.magnitude, timestamp: data.timestamp });
        if (eventCallbacksRef.current[foot]?.onJump) {
          eventCallbacksRef.current[foot].onJump(data);
        }
        break;

      case 'turn':
        addEvent(foot, { type: 'turn', timestamp: data.timestamp });
        if (eventCallbacksRef.current[foot]?.onTurn) {
          eventCallbacksRef.current[foot].onTurn(data);
        }
        break;

      case 'calibration':
        setDevices(prev => ({
          ...prev,
          [foot]: {
            ...prev[foot],
            calibrationState: data
          }
        }));
        addEvent(foot, { type: 'calibration', state: data.state, timestamp: data.timestamp });
        if (eventCallbacksRef.current[foot]?.onCalibration) {
          eventCallbacksRef.current[foot].onCalibration(data);
        }
        break;

      case 'status':
        setDevices(prev => ({
          ...prev,
          [foot]: {
            ...prev[foot],
            status: data,
            onPointe: data.onPointe
          }
        }));
        break;

      case 'gainReset':
      case 'gainAdjust':
      case 'gainDone':
        addEvent(foot, { type: 'gain', ...data, timestamp: data.timestamp });
        break;

      case 'error':
        console.error(`❌ ${foot} foot error:`, data.error);
        addEvent(foot, { type: 'error', error: data.error, timestamp: data.timestamp });
        break;

      case 'imuInit':
        if (!data.success) {
          console.error(`❌ ${foot} foot IMU init failed`);
        }
        break;

      default:
        break;
    }
  }, []);

  const addEvent = useCallback((foot, event) => {
    setEvents(prev => ({
      ...prev,
      [foot]: [...prev[foot].slice(-49), event]
    }));
  }, []);

  const handleDataReceived = useCallback((event, foot) => {
    const value = event.target.value;

    try {
      const parsedData = parseBluetoothData(value);

      if (!parsedData) {
        console.warn(`⚠️ Failed to parse data for ${foot} foot`);
        return;
      }

      if (parsedData.type === MESSAGE_TYPES.DATA_PACKET) {
        handleDataPacket(parsedData, foot);
      } else if (parsedData.type === MESSAGE_TYPES.TEXT_MESSAGE) {
        handleTextMessage(parsedData, foot);
      }
    } catch (error) {
      console.error(`Error processing data for ${foot} foot:`, error);
    }
  }, [handleDataPacket, handleTextMessage]);

  const handleDisconnection = useCallback((foot) => {
    console.log(`🔌 ${foot} foot device disconnected`);
    setDevices(prev => ({
      ...prev,
      [foot]: {
        device: null,
        isConnected: false,
        isRecording: false,
        sensorValues: {},
        imu: null,
        onPointe: false,
        lastMessage: null,
        calibrationState: null,
        status: null,
        characteristic: null
      }
    }));
    characteristicsRef.current[foot] = null;
    isRecordingRef.current[foot] = false;
    addEvent(foot, { type: 'disconnected', timestamp: Date.now() });
  }, [addEvent]);

  const getDeviceNameForFoot = (foot) => {
    const deviceSetting = foot === 'left' ? deviceSettings.leftFootDevice : deviceSettings.rightFootDevice;

    if (!deviceSetting) {
      return null;
    }

    if (/^\d+$/.test(deviceSetting)) {
      return `PSCore${deviceSetting}`;
    }

    return deviceSetting;
  };

  const connectDevice = async (selectedDevice, foot) => {
    const server = await selectedDevice.gatt.connect();
    const service = await server.getPrimaryService(BLE_CONFIG.serviceUUID);
    const characteristic = await service.getCharacteristic(BLE_CONFIG.characteristicUUID);
    const commandCharacteristic = await service.getCharacteristic(BLE_CONFIG.commandCharacteristicUUID);

    if (!characteristic.properties.notify) {
      throw new Error('Characteristic does not support notifications');
    }

    await characteristic.startNotifications();
    characteristic.addEventListener('characteristicvaluechanged',
      (event) => handleDataReceived(event, foot)
    );

    setDevices(prev => ({
      ...prev,
      [foot]: {
        ...prev[foot],
        device: selectedDevice,
        isConnected: true,
        characteristic: characteristic,
        commandCharacteristic: commandCharacteristic
      }
    }));

    characteristicsRef.current[foot] = {
      data: characteristic,
      command: commandCharacteristic
    };
    addEvent(foot, { type: 'connected', deviceName: selectedDevice.name, timestamp: Date.now() });

    selectedDevice.addEventListener('gattserverdisconnected',
      () => handleDisconnection(foot)
    );
  };

  const connectToDevice = async (foot) => {
    try {
      checkBluetoothSupport();
      setIsConnecting(prev => ({ ...prev, [foot]: true }));

      const deviceName = getDeviceNameForFoot(foot);

      if (!deviceName) {
        throw new Error(`No device configured for ${foot} foot. Please configure in settings.`);
      }

      console.log(`🔍 Looking for device: ${deviceName} for ${foot} foot`);

      let requestOptions;

      try {
        requestOptions = {
          filters: [{ name: deviceName }],
          optionalServices: [BLE_CONFIG.serviceUUID]
        };

        const selectedDevice = await navigator.bluetooth.requestDevice(requestOptions);
        console.log(`✅ Found device with exact name: ${selectedDevice.name}`);
        await connectDevice(selectedDevice, foot);

      } catch (exactNameError) {
        console.log(`Exact name match failed, trying prefix search...`);

        requestOptions = {
          filters: [{ namePrefix: 'PSCore' }],
          optionalServices: [BLE_CONFIG.serviceUUID]
        };

        const selectedDevice = await navigator.bluetooth.requestDevice(requestOptions);
        console.log(`✅ Found device with prefix: ${selectedDevice.name}`);

        if (selectedDevice.name !== deviceName) {
          console.warn(`⚠️ Warning: Connected to ${selectedDevice.name} instead of ${deviceName}`);
        }

        await connectDevice(selectedDevice, foot);
      }

    } catch (error) {
      console.error(`❌ Connection failed for ${foot} foot:`, error);
      throw error;
    } finally {
      setIsConnecting(prev => ({ ...prev, [foot]: false }));
    }
  };

  const disconnectDevice = (foot) => {
    const device = devices[foot].device;
    if (device && device.gatt.connected) {
      device.gatt.disconnect();
    }
    handleDisconnection(foot);
  };

  const setIsRecording = (foot, value) => {
    console.log(`🎬 Setting ${foot} foot recording state to:`, value);
    isRecordingRef.current[foot] = value;
    setDevices(prev => ({
      ...prev,
      [foot]: {
        ...prev[foot],
        isRecording: value
      }
    }));
    addEvent(foot, { type: value ? 'recordingStarted' : 'recordingStopped', timestamp: Date.now() });
  };

  const clearData = (foot) => {
    console.log(`🗑️ Clearing sensor data for ${foot} foot`);
    setDevices(prev => ({
      ...prev,
      [foot]: {
        ...prev[foot],
        sensorValues: {}
      }
    }));
  };

  const clearEvents = (foot) => {
    setEvents(prev => ({
      ...prev,
      [foot]: []
    }));
  };

  const sendCommandToDevice = async (foot, command) => {
    const characteristics = characteristicsRef.current[foot];
    if (!characteristics?.command) {
      throw new Error(`No command connection to ${foot} foot device`);
    }
    return sendCommand(characteristics.command, command);
  };

  const startCalibration = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.START_CALIBRATION);
  };

  const continueToPointe = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.CONTINUE_POINTE);
  };

  const cancelCalibration = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.CANCEL_CALIBRATION);
  };

  const resetGain = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.RESET_GAIN);
  };

  const getStatus = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.GET_STATUS);
  };

  const ping = async (foot) => {
    return sendCommandToDevice(foot, COMMANDS.PING);
  };

  const setEventCallbacks = (foot, callbacks) => {
    eventCallbacksRef.current[foot] = callbacks;
  };

  return {
    devices,
    isConnecting,
    events,
    connectToDevice,
    disconnectDevice,
    setIsRecording,
    clearData,
    clearEvents,
    sendCommand: sendCommandToDevice,
    startCalibration,
    continueToPointe,
    cancelCalibration,
    resetGain,
    getStatus,
    ping,
    setEventCallbacks,
    deviceSettings
  };
};
